import SLog from '@/shared/lib/SLog'
import JSZip from 'jszip'
import { toast } from 'sonner'

export interface DownloadableAsset {
    title?: string;
    fileName?: string;
    mimeType?: string;
    url?: string;
    previewUrl?: string;
}

type DownloadableGalleryAsset = {
    fileName: string
    url: string
}

function sanitizeFileNamePart(value: string) {
    return value.replace(/[\\/:*?"<>|]+/g, '_').trim()
}

function splitFileName(value: string) {
    const trimmed = value.trim()
    const lastDotIndex = trimmed.lastIndexOf('.')

    if (lastDotIndex <= 0 || lastDotIndex === trimmed.length - 1) {
        return {
            baseName: trimmed,
            extension: '',
        }
    }

    return {
        baseName: trimmed.slice(0, lastDotIndex),
        extension: trimmed.slice(lastDotIndex + 1),
    }
}

function normalizeFileName(value: string) {
    const { baseName, extension } = splitFileName(value)
    const normalizedBaseName = sanitizeFileNamePart(baseName) || Date.now().toString()
    const normalizedExtension = sanitizeFileNamePart(extension)

    return normalizedExtension
        ? `${normalizedBaseName}.${normalizedExtension}`
        : normalizedBaseName
}

function inferExtensionFromMimeType(mimeType?: string) {
    switch ((mimeType || '').split(';', 1)[0].trim().toLowerCase()) {
        case 'image/jpeg':
        case 'image/jpg':
            return 'jpg'
        case 'image/png':
            return 'png'
        case 'image/webp':
            return 'webp'
        case 'image/gif':
            return 'gif'
        case 'image/bmp':
            return 'bmp'
        case 'image/svg+xml':
            return 'svg'
        case 'image/avif':
            return 'avif'
        default:
            return ''
    }
}

function replaceFileExtension(fileName: string, extension: string) {
    const { baseName } = splitFileName(fileName)
    return `${baseName || fileName}.${extension}`
}

function resolveResponseFileName(fileName: string, blob: Blob) {
    const responseExtension = inferExtensionFromMimeType(blob.type)
    return responseExtension ? replaceFileExtension(fileName, responseExtension) : fileName
}

function assertDownloadableResponse(blob: Blob) {
    const mimeType = blob.type.split(';', 1)[0].trim().toLowerCase()
    if (mimeType === 'text/html' || mimeType === 'application/xml' || mimeType === 'text/xml') {
        throw new Error(`Unexpected response type: ${mimeType}`)
    }
}

function inferExtensionFromUrl(url: string) {
    try {
        const parsed = new URL(url)
        const lastPathSegment = decodeURIComponent(parsed.pathname.split('/').filter(Boolean).pop() ?? '')
        return sanitizeFileNamePart(splitFileName(lastPathSegment).extension)
    } catch {
        return ''
    }
}

function resolveAssetFileName(asset: DownloadableAsset) {
    const preferredFileName = asset.fileName?.trim()
    if (preferredFileName) {
        return normalizeFileName(preferredFileName)
    }

    const normalizedTitle = sanitizeFileNamePart(asset.title || '') || Date.now().toString()
    const extension = inferExtensionFromMimeType(asset.mimeType)
        || inferExtensionFromUrl(asset.url || asset.previewUrl || '')

    return extension ? `${normalizedTitle}.${extension}` : normalizedTitle
}

function resolveDownloadableAssets(assets: ReadonlyArray<DownloadableAsset | undefined>) {
    return assets.flatMap((asset) => {
        if (!asset) {
            return []
        }

        const url = asset.url || asset.previewUrl || ''
        if (!url) {
            return []
        }

        return [{
            fileName: resolveAssetFileName(asset),
            url,
        }]
    })
}

function withIndexedFileNames(items: ReadonlyArray<DownloadableGalleryAsset>) {
    const padWidth = Math.max(3, String(items.length).length)

    return items.map((item, index) => ({
        ...item,
        fileName: `${String(index + 1).padStart(padWidth, '0')}-${item.fileName}`,
    }))
}

/**
 * 单文件：使用 File System Access API 弹出目录选择框写入，或浏览器下载。
 * 多文件：批量获取后压缩为 zip 并通过浏览器下载。
 */
export async function batchDownloadAssets(assets: ReadonlyArray<DownloadableAsset | undefined>) {
    const items = resolveDownloadableAssets(assets)
    if (items.length === 0) {
        return { successCount: 0, failCount: 0, totalCount: 0 }
    }

    // 多文件：压缩为 zip 下载
    if (items.length > 1) {
        return downloadAsZip(items)
    }

    // 单文件：直接通过浏览器默认行为下载
    return downloadViaBrowser(items)
}

async function downloadAsZip(items: ReadonlyArray<DownloadableGalleryAsset>) {
    const toastId = toast.loading('正在打包下载中，请稍候...')
    const zip = new JSZip()
    const indexedItems = withIndexedFileNames(items)
    let successCount = 0
    let failCount = 0

    for (const { url, fileName } of indexedItems) {
        try {
            const response = await fetch(url)
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`)
            }
            const blob = await response.blob()
            assertDownloadableResponse(blob)
            zip.file(resolveResponseFileName(fileName, blob), blob)
            successCount += 1
        } catch {
            failCount += 1
        }
    }

    if (successCount === 0) {
        toast.dismiss(toastId)
        toast.error('下载失败')
        SLog.logEvent('batch_download', 'fail msg-获取所有资源失败');
        return { successCount: 0, failCount, totalCount: items.length }
    }

    try {
        const zipBlob = await zip.generateAsync({ type: 'blob' })
        const zipName = `qianluAI-${Date.now()}.zip`
        await downloadBlob(zipName, zipBlob)

        toast.dismiss(toastId)
        if (failCount === 0) {
            toast.success(`${successCount} 个资源已打包下载完成`)
        } else {
            toast.warning(`${successCount} 个资源已打包，${failCount} 个资源失败`)
        }
        SLog.logEvent('batch_download', 'suc');
    } catch (err) {
        toast.dismiss(toastId)
        toast.error('打包下载失败')
        SLog.logEvent('batch_download', `fail msg-${err instanceof Error ? err.message : '打包下载失败'}`);
        return { successCount: 0, failCount: items.length, totalCount: items.length }
    }

    return { successCount, failCount, totalCount: items.length }
}

async function downloadViaBrowser(items: ReadonlyArray<DownloadableGalleryAsset>) {
    const toastId = toast.loading('正在下载中，请稍候...')
    const { url, fileName } = items[0]

    try {
        await downloadRemoteFileWithName(url, fileName)
        toast.dismiss(toastId)
        toast.success('下载完成')
        SLog.logEvent('download', 'suc');
        return { successCount: 1, failCount: 0, totalCount: 1 }
    } catch (err) {
        toast.dismiss(toastId)
        toast.error('下载失败')
        SLog.logEvent('download', `fail msg-${err instanceof Error ? err.message : '下载失败'}`);
        return { successCount: 0, failCount: 1, totalCount: 1 }
    }
}

async function downloadRemoteFileWithName(url: string, fileName: string) {
    if (!url || typeof document === 'undefined' || typeof window === 'undefined') {
        return
    }

    const response = await fetch(url, { method: 'GET' })
    if (!response.ok) {
        throw new Error(`下载失败：${response.status}`)
    }

    const fileBlob = await response.blob()
    assertDownloadableResponse(fileBlob)
    await downloadBlob(resolveResponseFileName(fileName, fileBlob), fileBlob)
}

/** 通过创建临时链接触发浏览器下载 Blob，完成后释放 object URL */
async function downloadBlob(fileName: string, blob: Blob) {
    const objectUrl = URL.createObjectURL(blob)

    try {
        const link = document.createElement('a')
        link.href = objectUrl
        link.download = fileName
        link.rel = 'noopener'
        document.body.appendChild(link)
        link.click()
        link.remove()
    } finally {
        window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0)
    }
}
